import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { apiUrl, fetchJson, getApiBase } from '../apiBase';
import { ensureParentSession } from './parentSession';
import { ParentNavigationProvider } from './ParentNavigation';
import Student360Expired from './Student360Expired';

function createDiagnosticId() {
  const bytes = new Uint8Array(8);
  if (window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function reportFailure(diagnosticId, reason, page) {
  // Only fixed categories and a random correlation code leave the browser.
  fetch(apiUrl('/auth/session-diagnostic'), {
    method: 'POST', credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ diagnostic_id: diagnosticId, reason, page }),
  }).catch(() => {});
}

// Backend routes remain responsible for student and role authorization.
const RequireParent = ({ children }) => {
  const location = useLocation();
  const [status, setStatus] = useState('checking');
  const [attempt, setAttempt] = useState(0);
  const [diagnosticId, setDiagnosticId] = useState('');

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const trace = createDiagnosticId();
    setDiagnosticId(trace);
    setStatus('checking');
    const timeout = setTimeout(() => controller.abort(), 15000);

    function fail(reason) {
      if (cancelled) return;
      reportFailure(trace, reason, location.pathname);
      setStatus('error');
    }
    async function readSession() {
      if (controller.signal.aborted) throw new Error('Session check timed out');
      const res = await fetchJson(`/auth/session?diagnostic_id=${trace}`, {
        cache: 'no-store', dedupe: false, signal: controller.signal,
      });
      if (!res.ok) {
        fail(res.status === 401 ? 'unauthorized' : 'server_error');
        return null;
      }
      let js;
      try { js = await res.json(); } catch { fail('invalid_response'); return null; }
      if (typeof js?.authenticated !== 'boolean') { fail('invalid_response'); return null; }
      return js;
    }
    async function check() {
      try {
        await ensureParentSession(getApiBase());
        if (cancelled) return;
        let js = await readSession();
        if (!js || cancelled) return;
        if (!js.authenticated && js.can_refresh === true) {
          // Refresh once, then verify that the browser actually retained access.
          const refreshed = await fetch(apiUrl('/auth/refresh'), {
            method: 'POST', credentials: 'include', cache: 'no-store', signal: controller.signal,
          });
          if (cancelled) return;
          if (refreshed.status === 401 || refreshed.status === 403) { setStatus('denied'); return; }
          if (!refreshed.ok) { fail('server_error'); return; }
          js = await readSession();
          if (!js || cancelled) return;
        }
        if (!cancelled) setStatus(js.authenticated ? 'allowed' : 'denied');
      } catch (error) {
        if (!cancelled && (error.status === 401 || error.status === 403)) setStatus('denied');
        else fail('network_error');
      } finally {
        clearTimeout(timeout);
      }
    }
    check();
    return () => { cancelled = true; clearTimeout(timeout); controller.abort(); };
  }, [attempt, location.pathname]);

  if (status === 'checking') return null;
  if (status === 'error') return <Student360Expired connectionError diagnosticId={diagnosticId}
    onRetry={() => setAttempt((value) => value + 1)} />;
  if (status === 'denied') {
    const to = { pathname: '/student360/expired', search: '?reason=session_expired' };
    return <Navigate to={to} replace state={{ from: location, diagnosticId }} />;
  }
  return <ParentNavigationProvider>{children}</ParentNavigationProvider>;
};

export default RequireParent;
